import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import dayjs from 'dayjs'
import { cn } from '@/lib/cn'

export interface DatePickerMark {
  /** Dia no formato YYYY-MM-DD. */
  day: string
  /** Tom da marcação: normal, atenção ou falha. */
  tone?: 'ok' | 'warn' | 'error'
  /** Texto do tooltip do dia. */
  title?: string
}

interface Props {
  label?: string
  /** YYYY-MM-DD */
  value: string
  onChange: (value: string) => void
  /** Dias com marcação (ex.: dias que têm relatório). Vêm em cor diferente. */
  marks?: DatePickerMark[]
  /** Chamado quando o mês visível muda — para carregar as marcações daquele mês. */
  onMonthChange?: (month: string) => void
  /** Não permite escolher datas depois de hoje. Default: true. */
  maxToday?: boolean
  disabled?: boolean
  className?: string
  /** Texto de apoio abaixo do campo. */
  hint?: string
}

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

/**
 * Seletor de data com calendário e dias marcados.
 *
 * Nasceu para a tela de sincronização da Medicar: o operador abre o
 * calendário já no dia de hoje e enxerga, em cor diferente, os dias que têm
 * relatório. Genérico o bastante para qualquer tela que precise "escolher um
 * dia entre os que têm algo".
 *
 * Painel via portal com `position: fixed`, como o Select e o AsyncSelect —
 * escapa de ancestrais com overflow.
 */
export function DatePicker({
  label, value, onChange, marks = [], onMonthChange, maxToday = true, disabled, className, hint,
}: Props) {
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => dayjs(value || undefined).startOf('month'))
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const selected = value ? dayjs(value) : null
  const today = dayjs().startOf('day')
  const markByDay = new Map(marks.map(m => [m.day, m]))

  // Ao abrir, volta para o mês da data escolhida e avisa quem carrega as marcações.
  useEffect(() => {
    if (!open) return
    const m = (selected ?? today).startOf('month')
    setMonth(m)
    onMonthChange?.(m.format('YYYY-MM'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const recalc = () => {
    const r = triggerRef.current?.getBoundingClientRect()
    if (!r) return
    const panelH = panelRef.current?.offsetHeight ?? 320
    const flipUp = window.innerHeight - r.bottom < panelH + 12 && r.top > panelH + 12
    setPos({ top: flipUp ? r.top - panelH - 4 : r.bottom + 4, left: r.left })
  }
  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const id = requestAnimationFrame(recalc)
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, month])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', recalc, true)
    window.addEventListener('resize', recalc)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', recalc, true)
      window.removeEventListener('resize', recalc)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function goMonth(delta: number) {
    const m = month.add(delta, 'month')
    setMonth(m)
    onMonthChange?.(m.format('YYYY-MM'))
  }

  function pick(d: dayjs.Dayjs) {
    onChange(d.format('YYYY-MM-DD'))
    setOpen(false)
  }

  // Grade: começa no domingo da semana do dia 1 e vai até fechar 6 linhas.
  const first = month.startOf('month')
  const gridStart = first.subtract(first.day(), 'day')
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'))

  return (
    <div className={cn('flex flex-col', className)}>
      {label && <label className="mb-1 text-xs font-medium text-gray-700">{label}</label>}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={cn(
          'inline-flex w-full cursor-pointer items-center gap-2 rounded-md bg-white px-3 py-2 text-sm ring-1 transition',
          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
          open ? 'ring-2 ring-brand-500' : 'ring-gray-200 hover:ring-gray-300',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarDays size={15} className="shrink-0 text-gray-400" />
        <span className={cn('flex-1 text-left', selected ? 'text-gray-900' : 'text-gray-400')}>
          {selected ? selected.format('DD/MM/YYYY') : 'Selecione uma data'}
        </span>
      </button>
      {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-[280px] rounded-md bg-white p-3 shadow-lg ring-1 ring-gray-200"
        >
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => goMonth(-1)} aria-label="Mês anterior"
              className="cursor-pointer rounded p-1 text-gray-500 transition hover:bg-gray-100">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-gray-800">
              {MONTHS[month.month()]} {month.year()}
            </span>
            <button type="button" onClick={() => goMonth(1)} aria-label="Próximo mês"
              disabled={maxToday && month.add(1, 'month').isAfter(today, 'month')}
              className="cursor-pointer rounded p-1 text-gray-500 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((w, i) => (
              <span key={i} className="py-1 text-[10px] font-medium uppercase text-gray-400">{w}</span>
            ))}
            {cells.map(d => {
              const key = d.format('YYYY-MM-DD')
              const inMonth = d.month() === month.month()
              const isSelected = selected?.isSame(d, 'day') ?? false
              const isToday = d.isSame(today, 'day')
              const future = maxToday && d.isAfter(today, 'day')
              const mark = markByDay.get(key)
              const tone = mark?.tone ?? 'ok'

              return (
                <button
                  key={key}
                  type="button"
                  disabled={future}
                  onClick={() => pick(d)}
                  title={mark?.title}
                  className={cn(
                    'relative mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-xs transition',
                    'disabled:cursor-not-allowed disabled:opacity-30',
                    !inMonth && 'text-gray-300',
                    inMonth && !mark && !isSelected && 'text-gray-700 hover:bg-gray-100',
                    // Dias com relatório: cor própria, para saltarem aos olhos.
                    mark && !isSelected && tone === 'ok' && 'bg-emerald-100 font-semibold text-emerald-800 hover:bg-emerald-200',
                    mark && !isSelected && tone === 'warn' && 'bg-amber-100 font-semibold text-amber-800 hover:bg-amber-200',
                    mark && !isSelected && tone === 'error' && 'bg-rose-100 font-semibold text-rose-800 hover:bg-rose-200',
                    isSelected && 'bg-brand-600 font-semibold text-white',
                    isToday && !isSelected && 'ring-1 ring-brand-400',
                  )}
                >
                  {d.date()}
                  {mark && isSelected && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-white" />}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 text-[10px] text-gray-500">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-200" /> com relatório</span>
            <button type="button" onClick={() => pick(today)}
              className="cursor-pointer rounded px-1.5 py-0.5 text-brand-600 transition hover:bg-brand-50">
              Hoje
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
